import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useEngine } from '@/contexts/EngineContext';
import { DashboardSkeleton, Shimmer } from '@/components/Shimmer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dumbbell, Zap, ArrowRight, RefreshCw, Sparkles, Lock,
  Clock, Play, Activity, CheckCircle2, AlertCircle, Battery, ArrowDown,
  ArrowUp, Minus, Crosshair, ChevronRight, TrendingUp, Flame, Bell,
  BatteryCharging, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { CoachMessageCard } from '@/components/CoachMessage';
import { useLocation } from 'react-router-dom';
import { formatSessionLabel, getSessionSubtitle } from '@/utils/formatSessionType';
import { FOCUS_AREA_OPTIONS } from '@/utils/focusAreaUtils';
import { formatGoal } from '@/lib/textHygiene';
import { useInstall } from '@/contexts/InstallContext';
import { DashboardInstallCard } from '@/components/InstallPrompt';
import { LevelUpPopup } from '@/components/LevelUpPopup';
import { ProgramCompletePopup } from '@/components/ProgramCompletePopup';
import { HomeHeroCarousel } from '@/components/HomeHeroCarousel';
import { RecoveryTiles, useRecoveryLog } from '@/components/RecoveryTiles';
import { RunnerTrailIcon } from '@/components/RunnerTrailIcon';
import { RecoveryWeekCard } from '@/components/RecoveryWeekCard';
import { ProgramCarousel, ProgramProgressStrip } from '@/components/ProgramCarousel';
import { ExploreCarousel } from '@/components/ExploreCarousel';
import { DEMO_CALORIE_STATS, DEMO_CARDIO_STATS } from '@/utils/demoStats';
import { StatsCarousel } from '@/components/StatsCarousel';
import { CaloriesCard } from '../components/CaloriesCard';
import { CardioCard } from '../components/CardioCard';
import { SnapshotShell, SnapshotHeader, SnapshotStat, SnapshotChip, RegenerateButton } from '../components/SnapshotCard';
import { StrengthGainRow } from '@/components/StrengthGainRow';
import { flushSubmitLog } from '@/utils/onboardingSubmitLog';
import { CalibrationBadge } from '@/components/CalibrationBadge';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function FatigueTrend({ trend }) {
  const config = {
    improving: { icon: ArrowDown, label: 'Improving', color: 'text-green-400', bg: 'bg-green-400/10' },
    rising:    { icon: ArrowUp,   label: 'Rising',    color: 'text-orange-400', bg: 'bg-orange-400/10' },
    stable:    { icon: Minus,     label: 'Stable',    color: 'text-accent',  bg: 'bg-accent/10' },
  };
  const t = config[trend] || config.stable;
  const Icon = t.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${t.color} ${t.bg}`} data-testid="fatigue-trend">
      <Icon size={11} strokeWidth={2.5} />
      {t.label}
    </span>
  );
}

// Calibration step dots — 4 steps, filled based on sessions completed
function CalibrationSteps({ completed, total = 4 }) {
  return (
    <div className="flex items-center gap-1.5" data-testid="calibration-steps">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-2 flex-1 rounded-full transition-all ${
            i < completed ? 'bg-accent' : 'bg-muted'
          }`}
        />
      ))}
    </div>
  );
}

const CARD_SHADOW = { boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 16px 40px rgba(0,0,0,0.45)' };

function SectionLabel({ children }) {
  return (
    <div className="flex items-center gap-3 pt-3">
      <span className="text-sm md:text-base font-heading font-black uppercase tracking-[0.18em] text-white">{children}</span>
      <div
        className="h-[2px] flex-1 rounded-full"
        style={{ background: 'linear-gradient(to right, rgba(var(--accent-rgb),0.08), rgba(var(--accent-rgb),0.45) 45%, var(--accent-hex))' }}
      />
    </div>
  );
}

const DEMO_STRENGTH_GAINS = [
  { exercise: 'Barbell Bench Press', equipment: 'barbell', first_weight: 40, current_weight: 52.5, gain_kg: 12.5, sessions: 12 },
  { exercise: 'Cable Lat Pulldown', equipment: 'cable', first_weight: 50, current_weight: 65, gain_kg: 15, sessions: 10 },
  { exercise: 'Dumbbell Shoulder Press', equipment: 'dumbbell', first_weight: 12, current_weight: 17.5, gain_kg: 5.5, sessions: 8 },
];

export default function Dashboard() {
  const { user, api, subscriptionStatus } = useAuth();
  const recoveryLog = useRecoveryLog();
  const engine = useEngine();
  const { markDashboardVisit } = useInstall();
  const location = useLocation();
  const [workout, setWorkout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [hasCoachAction, setHasCoachAction] = useState(false);
  const [brainStatus, setBrainStatus] = useState(null);
  const [focusAreas, setFocusAreas] = useState([]);
  const [focusMode, setFocusMode] = useState('skipped');
  const [weeklyStats, setWeeklyStats] = useState(null);
  const [strengthGains, setStrengthGains] = useState([]);
  const [calorieStats, setCalorieStats] = useState(null);
  const [cardioStats, setCardioStats] = useState(null);
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const [recoveryContext, setRecoveryContext] = useState(null);
  const [animatedCal, setAnimatedCal] = useState(0);
  const [chartKey, setChartKey] = useState(0);
  // False until the bundle has been applied (or failed), so we never mistake
  // "still loading" for "brand new user" and flash the demo datasets.
  const [statsLoaded, setStatsLoaded] = useState(false);

  // Demo mode: show sample data when user has no real workout history
  const hasRealStrengthData = strengthGains.length > 0;
  const hasRealCalorieData = calorieStats && (calorieStats.week_total_cal > 0 || calorieStats.last_session_cal > 0);
  const hasRealCardioData = !!cardioStats?.has_history;
  const isNewUser = statsLoaded && !hasRealStrengthData && !hasRealCalorieData && !hasRealCardioData;
  const statsPending = !statsLoaded && !hasRealStrengthData && !hasRealCalorieData && !hasRealCardioData;

  const displayStrengthGains = useMemo(() => hasRealStrengthData ? strengthGains : (isNewUser ? DEMO_STRENGTH_GAINS : []), [hasRealStrengthData, strengthGains, isNewUser]);
  // Mirrors the cardio card: a brand new user gets the sample week, not an all-zero card.
  const showCalorieDemo = !hasRealCalorieData && isNewUser;
  const displayCalorieStats = useMemo(
    () => (hasRealCalorieData ? calorieStats : (isNewUser ? DEMO_CALORIE_STATS : calorieStats)),
    [hasRealCalorieData, calorieStats, isNewUser],
  );

  // Cardio demo mirrors the calories card: sample numbers until the first cardio is logged,
  // then the card switches to real data for good (and back to demo if the data is ever reset).
  const showCardioDemo = !hasRealCardioData && isNewUser;
  const displayCardioStats = useMemo(() => {
    if (hasRealCardioData) return cardioStats;
    if (showCardioDemo) return DEMO_CARDIO_STATS;
    return (cardioStats?.target_minutes || 0) > 0 ? cardioStats : null;
  }, [hasRealCardioData, showCardioDemo, cardioStats]);

  // The calories card mounts only once its data arrives, so framer's useInView
  // (which observes on mount) would never attach. Observe via a callback ref instead.
  const [calorieEl, setCalorieEl] = useState(null);
  const [calorieInView, setCalorieInView] = useState(false);
  useEffect(() => {
    if (!calorieEl) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setCalorieInView(true); io.disconnect(); }
    }, { threshold: 0.2 });
    io.observe(calorieEl);
    return () => io.disconnect();
  }, [calorieEl]);

  // Animate calorie counter when card scrolls into view
  useEffect(() => {
    if (!displayCalorieStats) return;
    const target = displayCalorieStats.week_total_cal || 0;
    
    if (!calorieInView) {
      // Data ready but not in view yet — show target immediately (no 0 flash)
      setAnimatedCal(target);
      return;
    }
    
    // In view — animate from 0 to target
    if (target === 0) { setAnimatedCal(0); return; }
    setChartKey(k => k + 1);
    setAnimatedCal(0);
    const startTime = Date.now();
    const duration = 1500;
    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimatedCal(Math.round(target * eased));
      if (progress >= 1) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [calorieInView, displayCalorieStats]);
  const [completedSessions, setCompletedSessions] = useState([]);
  const [showNotifBanner, setShowNotifBanner] = useState(false);

  // Check if user needs notification prompt
  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      // User hasn't been asked yet — show banner
      const dismissed = localStorage.getItem('notif_banner_dismissed');
      if (!dismissed) setShowNotifBanner(true);
    }
  }, []);

  // Refetch everything on every navigation to this page
  // location.key changes on each navigate() call, ensuring fresh data
  useEffect(() => {
    // Load cached data instantly, then refresh from server
    markDashboardVisit();
    flushSubmitLog();  // send any submit report that never left the device
    const cached = localStorage.getItem('dashboard_cache');
    if (cached) {
      try {
        const c = JSON.parse(cached);
        if (c.workout) setWorkout(c.workout);
        if (c.brain_state) setBrainStatus(prev => ({ ...(prev || {}), brain_state: c.brain_state }));
        if (c.focus_areas) { setFocusAreas(c.focus_areas.focus_areas || []); setFocusMode(c.focus_areas.focus_area_mode || 'skipped'); }
        if (c.weekly_stats) setWeeklyStats(c.weekly_stats);
        if (c.strength_gains) setStrengthGains(c.strength_gains);
        if (c.calorie_stats) setCalorieStats(c.calorie_stats);
        if (c.cardio_stats) setCardioStats(c.cardio_stats);
        setLoading(false);
      } catch {}
    }
    fetchDashboardBundle();
    fetchBrainStatus();   // calibration phase lives here, not in the bundle
    
    // Request push notification permission after a short delay (non-blocking)
    // Only prompts once — browser remembers the decision
    const pushTimer = setTimeout(async () => {
      try {
        const { initOneSignal, oneSignalLogin, setOneSignalEmail, requestPushPermission, setOneSignalNotificationMode } = await import('@/utils/oneSignalManager');

        // Always reconcile: init + login so WTN/Android native push is linked to user
        await initOneSignal();
        if (user?.id) await oneSignalLogin(user.id);
        if (user?.email) await setOneSignalEmail(user.email);

        // First-time permission prompt (browser only — WTN handles natively)
        if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
          await requestPushPermission();
          
          // If permission granted, auto-enable subtle mode so scheduler sends notifications
          if (Notification.permission === 'granted') {
            await setOneSignalNotificationMode('subtle');
            await api.post('/notifications/settings', { mode: 'subtle', push_enabled: true, preferred_training_times: [] }).catch(() => {});
          }
        }
      } catch {}
    }, 3000);
    
    return () => clearTimeout(pushTimer);
  }, [location.key]);

  const fetchDashboardBundle = async () => {
    try {
      const res = await api.get('/dashboard/bundle');
      const d = res.data;
      if (d.workout) setWorkout(d.workout);
      if (d.brain_state) setBrainStatus(prev => ({ ...(prev || {}), brain_state: d.brain_state }));
      if (d.focus_areas) { setFocusAreas(d.focus_areas.focus_areas || []); setFocusMode(d.focus_areas.focus_area_mode || 'skipped'); }
      if (d.weekly_stats) setWeeklyStats(d.weekly_stats);
      if (d.strength_gains) setStrengthGains(d.strength_gains);
      if (d.calorie_stats) {
        setCalorieStats(d.calorie_stats);
      }
      if (d.cardio_stats) setCardioStats(d.cardio_stats);
      if (d.recovery_context) setRecoveryContext(d.recovery_context);
      if (d.completed_sessions) setCompletedSessions(d.completed_sessions);
      // Cache for next visit
      localStorage.setItem('dashboard_cache', JSON.stringify(d));
      setStatsLoaded(true);
    } catch (err) {
      // Fallback: just load the workout
      fetchWorkout();
      // Only drop the skeleton if the cache already gave us real stats to show
      try {
        const c = JSON.parse(localStorage.getItem('dashboard_cache') || '{}');
        if (c.strength_gains || c.calorie_stats || c.cardio_stats) setStatsLoaded(true);
      } catch {}
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkout = async () => {
    try {
      const res = await api.get('/workouts/active');
      setWorkout(res.data.workout);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBrainStatus = async () => {
    try {
      const res = await api.get('/brain/status');
      setBrainStatus(prev => ({ ...(prev || {}), ...res.data }));
    } catch (err) {}
  };

  const fetchFocusAreas = async () => {
    try {
      const res = await api.get('/user/focus-areas');
      setFocusAreas(res.data.focus_areas || []);
      setFocusMode(res.data.focus_area_mode || 'skipped');
    } catch (err) {}
  };

  const fetchWeeklyStats = async () => {
    try {
      const res = await api.get('/user/weekly-stats');
      setWeeklyStats(res.data);
    } catch (err) {}
  };

  const generateWorkout = useCallback(() => {
    if (generating || engine.visible) return;
    setGenerating(true);
    engine.trigger({
      subtitle: workout ? 'Rebuilding your training program' : 'Building your first personalized program',
      generationFn: async () => {
        const res = await api.post('/workouts/generate');
        return res.data;
      },
      onSuccess: (data) => {
        setWorkout(data);
        fetchBrainStatus();
        fetchWeeklyStats();
      },
      onError: () => {
        toast.error('Failed to generate workout');
      },
      onDismiss: () => {
        setGenerating(false);
      },
    });
  }, [generating, engine, workout, api]);

  const profile = user?.profile;
  const todayIdx = new Date().getDay();
  const todayDayIndex = todayIdx === 0 ? 6 : todayIdx - 1;
  const todayWorkout = workout?.days?.[todayDayIndex];
  const isRestDay = !todayWorkout || todayWorkout.type === 'Rest' || todayWorkout.type?.toLowerCase().includes('rest');

  // Find next training day (for rest day "Next Up")
  const getNextTrainingDay = () => {
    if (!workout?.days) return null;
    for (let offset = 1; offset <= 7; offset++) {
      const idx = (todayDayIndex + offset) % 7;
      const day = workout.days[idx];
      if (day && day.type !== 'Rest' && !day.type?.toLowerCase().includes('rest') && day.main?.length > 0) {
        const dayOfWeek = (todayIdx + offset) % 7;
        return { day, dayName: WEEKDAYS[dayOfWeek], dayIndex: idx };
      }
    }
    return null;
  };
  const nextTraining = getNextTrainingDay();

  // Brain state derived values
  const bs = brainStatus?.brain_state || {};
  const calibrationStatus = bs.calibration_status || brainStatus?.calibration?.status;
  const isCalibrating = calibrationStatus === 'in_calibration';
  const calibrationMetrics = bs.calibration_metrics || {};
  const sessionsCompleted = calibrationMetrics.sessions_completed || bs.sessions_in_calibration || 0;
  const fatigueScore = bs.fatigue_score || 0;
  const recoveryScore = bs.recovery_score || 100;
  const fatigueTrend = (() => {
    const raw = calibrationMetrics.fatigue_trend;
    if (typeof raw === 'string' && raw) return raw;
    if (Array.isArray(raw) && raw.length > 1) {
      const diff = raw[raw.length - 1] - raw[0];
      if (diff > 5) return 'rising';
      if (diff < -5) return 'easing';
    }
    return 'stable';
  })();
  const splitType = typeof brainStatus?.split === 'string'
    ? brainStatus.split
    : (brainStatus?.split?.split_type || null);

  const hasFocusAreas = focusAreas.length > 0 && focusMode === 'specific';
  const focusLabels = focusAreas.map(id => FOCUS_AREA_OPTIONS.find(o => o.id === id)?.label || id);

  // Dashboard layout

  if (loading && !workout) return <DashboardSkeleton />;

  return (
    <div className="min-h-screen app-page-bg" data-testid="dashboard-page">
      <LevelUpPopup />
      <ProgramCompletePopup />

      <main className="max-w-3xl md:max-w-4xl lg:max-w-5xl mx-auto px-5 md:px-8 pt-3 pb-24 space-y-8">

        {/* ═══ Hero Carousel (top of page) ═══ */}
        <div
          className="rounded-3xl overflow-hidden border-2 border-white/[0.18] relative" style={CARD_SHADOW}
        >
          <HomeHeroCarousel />
        </div>

        {/* ═══ Access Banners ═══ */}
        {subscriptionStatus?.status === 'prelaunch_trial' && subscriptionStatus?.days_left != null && (
          <div className="px-4 py-2.5 rounded-2xl bg-[#141414] border border-white/5 flex items-center gap-2" data-testid="dashboard-trial-banner">
            <Sparkles size={14} className="text-accent shrink-0" />
            <span className="text-xs font-medium text-zinc-300">
              {subscriptionStatus.days_left > 0
                ? `${subscriptionStatus.days_left} day${subscriptionStatus.days_left !== 1 ? 's' : ''} of free access remaining`
                : 'Less than 24h of free access remaining'}
            </span>
          </div>
        )}
        {subscriptionStatus?.status === 'manual_access' && subscriptionStatus?.days_left != null && (
          <div className="px-4 py-2.5 rounded-2xl bg-[#141414] border border-white/5 flex items-center gap-2" data-testid="dashboard-manual-banner">
            <Sparkles size={14} className="text-accent shrink-0" />
            <span className="text-xs font-medium text-zinc-300">
              {subscriptionStatus.days_left > 0
                ? `${subscriptionStatus.days_left} day${subscriptionStatus.days_left !== 1 ? 's' : ''} of access remaining`
                : 'Less than 24h of access remaining'}
            </span>
          </div>
        )}
        {(subscriptionStatus?.status === 'prelaunch_trial_ended' || subscriptionStatus?.status === 'manual_access_expired') && (
          <div className="px-4 py-2.5 rounded-2xl bg-[#141414] border border-amber-500/20 flex items-center gap-2" data-testid="dashboard-trial-expired">
            <Lock size={14} className="text-amber-500 shrink-0" />
            <span className="text-xs text-amber-400">Your free access has ended.</span>
          </div>
        )}

        {/* ═══ Coach Message (Pro only) ═══ */}
        {subscriptionStatus?.has_access && subscriptionStatus?.status === 'paid' && (
          <CoachMessageCard onHasActions={setHasCoachAction} />
        )}

        {/* ═══ TODAY HERO ═══ */}
        <SectionLabel>Today</SectionLabel>
        <ProgramProgressStrip api={api} />
        <div
          className="rounded-3xl overflow-hidden bg-gradient-to-b from-white/[0.04] to-white/[0.01] border border-white/[0.08]"
          style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08), 0 20px 48px rgba(0,0,0,0.55)' }}
          data-testid="today-hero-card"
        >
          {loading ? (
            <div className="p-5 space-y-3">
              <div className="h-5 bg-[#1F1F1F] rounded animate-pulse w-1/3" />
              <div className="h-3 bg-[#1F1F1F] rounded animate-pulse w-2/3" />
              <div className="h-12 bg-[#1F1F1F] rounded-xl animate-pulse mt-4" />
            </div>
          ) : !workout ? (
            /* ── No plan ── */
            <div className="p-6 flex flex-col items-center text-center space-y-4">
              <div className="w-14 h-14 rounded-2xl bg-[#1F1F1F] flex items-center justify-center">
                <Dumbbell size={28} className="text-accent" />
              </div>
              <div>
                <p className="font-heading font-semibold text-base text-white">No program yet</p>
                <p className="text-xs text-[#A3A3A3] mt-1">Generate your personalized plan to start training.</p>
              </div>
              <Button
                onClick={generateWorkout} disabled={generating}
                className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-2xl px-8 gap-2 font-bold text-sm h-12 w-full active:scale-[0.98] transition-all"
                data-testid="generate-workout-btn"
              >
                {generating ? <><RefreshCw size={16} className="animate-spin" /> Generating...</> : <><Zap size={16} /> Generate My Plan</>}
              </Button>
            </div>
          ) : isRestDay ? (
            /* ── Rest Day ── */
            <div className="relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(var(--accent-rgb),0.1) 0%, transparent 70%)' }} />
              <div className="p-5 relative">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2.5">
                    <span className="text-xs font-mono font-bold text-[#A3A3A3] uppercase tracking-wider">{WEEKDAYS[todayIdx]}</span>
                  </div>
                </div>

                <div className="text-left">
                  <h2
                    className="font-heading font-black uppercase leading-[0.84] tracking-[-0.015em]"
                    data-testid="today-workout-title"
                  >
                    <span className="block text-white text-[clamp(2rem,10.5vw,2.75rem)]">Rest &amp;</span>
                    <span
                      className="block text-[clamp(2rem,10.5vw,2.75rem)] bg-clip-text text-transparent"
                      style={{ backgroundImage: 'linear-gradient(180deg, #FFFFFF 0%, #E8EDF5 42%, #8A93A3 100%)' }}
                    >
                      Recover
                    </span>
                  </h2>
                  <p className="text-sm text-[#A3A3A3] mt-2.5" data-testid="recovery-tip">
                    {recoveryContext?.volume_kg > 0
                      ? `You lifted ${recoveryContext.volume_kg.toLocaleString()}kg ${recoveryContext.day_label || 'last session'} — today your muscles rebuild.`
                      : 'Your muscles are rebuilding — growth happens now.'}
                  </p>
                </div>

                <div className="mt-5 mb-5" data-testid="recovery-checklist">
                  <RecoveryTiles log={recoveryLog} />
                </div>

                {nextTraining && (
                  <Link to={`/workout/${workout.id}`} className="block">
                    <div
                      className="relative overflow-hidden rounded-2xl border border-accent/25 transition-transform active:scale-[0.99]"
                      style={{ boxShadow: 'inset 0 1px 0 rgba(var(--accent-rgb),0.12), 0 8px 24px rgba(0,0,0,0.5)' }}
                      data-testid="next-up-card"
                    >
                      <img
                        src="/upnext-bg.webp"
                        alt=""
                        className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-70"
                        loading="lazy"
                        decoding="async"
                        data-testid="next-up-bg"
                      />
                      <span className="pointer-events-none absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(5,7,3,0.96) 0%, rgba(5,7,3,0.82) 45%, rgba(5,7,3,0.25) 100%)' }} />
                      <span className="pointer-events-none absolute inset-y-0 left-0 w-1" style={{ background: 'linear-gradient(180deg, rgba(var(--accent-rgb),0.85), var(--accent-grad-a))' }} />

                      <div className="relative flex items-center gap-3 p-4 pl-5">
                        <div className="min-w-0 flex-1">
                          <p className="mb-1 flex items-center gap-1.5 font-heading text-[10px] font-black uppercase tracking-[0.22em] text-accent">
                            Up Next
                            <span className="h-px w-4 bg-accent/40" />
                            {nextTraining.dayName}
                          </p>
                          <p className={`truncate font-heading font-black uppercase leading-none tracking-tight text-white ${
                            (formatSessionLabel(nextTraining.day) || '').length > 14 ? 'text-[20px]' : 'text-[26px]'
                          }`} style={{ textShadow: '0 2px 14px rgba(0,0,0,0.75)' }}>
                            {formatSessionLabel(nextTraining.day)}
                          </p>
                          <div className="mt-2.5 flex flex-wrap items-center gap-2">
                            <span className="flex items-center gap-1.5 rounded-full border border-white/[0.12] bg-black/55 px-2.5 py-1 text-[11px] font-medium text-white/75 backdrop-blur-sm">
                              <Dumbbell size={11} className="text-accent" />
                              {nextTraining.day.main?.length || 0} {(nextTraining.day.main?.length || 0) === 1 ? 'exercise' : 'exercises'}
                            </span>
                            {nextTraining.day.session_length && (
                              <span className="flex items-center gap-1.5 rounded-full border border-white/[0.12] bg-black/55 px-2.5 py-1 text-[11px] font-medium text-white/75 backdrop-blur-sm">
                                <Clock size={11} className="text-accent" />
                                {nextTraining.day.session_length} min
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-accent/40 bg-accent/15 backdrop-blur-sm">
                          <ChevronRight size={20} className="text-accent" />
                        </span>
                      </div>
                    </div>
                  </Link>
                )}
              </div>
            </div>
          ) : (
            /* ── Training Day ── */
            <div>
              <div className="px-5 pb-5 pt-5">
              <div className="flex items-start gap-3 mb-4">
                <h2 className="text-3xl sm:text-4xl font-heading font-black uppercase tracking-tight leading-[0.95] text-white" data-testid="today-workout-title">Today's Session</h2>
                <span className="ml-auto mt-1 px-2.5 py-1 rounded-lg text-[10px] font-heading font-black uppercase tracking-wider bg-accent text-accent-foreground shrink-0" data-testid="session-type-badge">
                  {formatSessionLabel(todayWorkout)}
                </span>
              </div>

              {/* Meta chips */}
              <div className="flex items-center gap-1.5 mb-5 flex-wrap">
                <span className="px-2.5 py-1 rounded-full border border-white/[0.08] bg-white/[0.03] text-xs text-white/60 uppercase tracking-wide">{WEEKDAYS[todayIdx]}</span>
                <span className="px-2.5 py-1 rounded-full border border-white/[0.08] bg-white/[0.03] text-xs text-white/60 flex items-center gap-1.5"><Dumbbell size={11} className="text-white/40" />{todayWorkout?.main?.length || 0} exercises</span>
                {todayWorkout?.session_length && <span className="px-2.5 py-1 rounded-full border border-white/[0.08] bg-white/[0.03] text-xs text-white/60 flex items-center gap-1.5"><Clock size={11} className="text-white/40" />{todayWorkout.session_length} min</span>}
                {todayWorkout?.cardio_block && <span className="px-2.5 py-1 rounded-full text-xs font-medium text-white flex items-center gap-1.5" style={{ background: '#0165FE', boxShadow: '0 4px 14px rgba(1,101,254,0.35)' }} data-testid="today-cardio-chip"><RunnerTrailIcon size={13} />Cardio</span>}
              </div>

              {/* Exercise preview — same scale as Strength Gains rows */}
              <div className="space-y-3 mb-6 md:space-y-0 md:grid md:grid-cols-2 md:gap-3">
                {todayWorkout?.main?.slice(0, 5).map((ex, i) => (
                  <div key={i} className="flex items-center gap-3" data-testid={`today-exercise-${i}`}>
                    <div className="w-8 h-8 rounded-full bg-white/[0.05] border border-white/[0.07] flex items-center justify-center shrink-0">
                      <span className="text-xs font-heading font-black text-white/50">{i + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-heading font-bold text-white truncate">{ex.exercise}</p>
                      {ex.primary_muscle && <p className="text-xs text-white/40 capitalize">{(ex.primary_muscle || '').replace('_', ' ')}</p>}
                    </div>
                    <p className="text-base font-heading font-black text-accent shrink-0">{ex.sets}×{ex.reps}</p>
                  </div>
                ))}
                {(todayWorkout?.main?.length || 0) > 5 && (
                  <p className="text-xs text-white/40 text-center pt-1">+{todayWorkout.main.length - 5} more</p>
                )}
              </div>

              <Link
                to={`/workout/${workout.id}${subscriptionStatus?.has_access === false ? '?focus=demo' : ''}`}
                className="relative block w-full mx-auto aspect-[925/186] rounded-2xl font-heading font-black uppercase tracking-wider text-[22px] min-[360px]:text-2xl text-white flex items-center justify-center active:scale-[0.97] transition-transform"
                style={{ background: 'radial-gradient(120% 140% at 50% 0%, #0C3A74 0%, #061427 70%)', boxShadow: 'inset 0 0 0 1.5px rgba(79,169,255,0.9), 0 0 24px rgba(10,132,255,0.55), inset 0 0 26px rgba(10,132,255,0.35)', textShadow: '0 0 12px rgba(124,212,255,0.9)' }}
                aria-label="Begin session"
                data-testid="view-workout-btn"
              >
                Start Training
              </Link>
            </div>
            </div>
          )}
        </div>

        {/* ═══ STRENGTH / CALORIES / CARDIO — placeholders until the bundle lands ═══ */}
        {statsPending && (
          <div className="space-y-5" data-testid="dashboard-stats-skeleton">
            <SectionLabel>Progress</SectionLabel>
            <Shimmer className="h-56 rounded-3xl" />
            <Shimmer className="h-72 rounded-3xl" />
            <Shimmer className="h-48 rounded-3xl" />
          </div>
        )}

        {/* ═══ STRENGTH GAINS ═══ */}
        {displayStrengthGains.length > 0 && (<>
          <SectionLabel>Progress</SectionLabel>
          <div
            className="relative rounded-3xl overflow-hidden border border-white/[0.08] bg-gradient-to-b from-white/[0.04] to-white/[0.01]"
            style={CARD_SHADOW}
            data-testid="strength-gains-card"
          >
            <div className="relative z-[1] px-5 pb-5 pt-5">
              <div className="flex items-start gap-2 mb-4">
                <h3 className="text-3xl sm:text-4xl font-heading font-black uppercase tracking-tight leading-[0.95] text-white">Strength Gains</h3>
                {!hasRealStrengthData && <span className="ml-auto mt-1 text-[10px] font-heading font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-accent/15 text-accent font-black">Demo</span>}
              </div>
              <div className="space-y-3 md:space-y-0 md:grid md:grid-cols-2 md:gap-3">
                {displayStrengthGains.map((g, i) => (
                  <StrengthGainRow key={i} gain={g} index={i} testId={`strength-gain-${i}`} />
                ))}
              </div>
            </div>
          </div>
        </>)}

        {/* ═══ CALORIES BURNED ═══ */}
        {displayCalorieStats && (<>
          <SectionLabel>Calories</SectionLabel>
          <motion.div
            ref={setCalorieEl}
            initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false, amount: 0.2 }} transition={{ delay: 0.2 }}
            className="relative rounded-3xl overflow-hidden border border-white/[0.08] bg-gradient-to-b from-white/[0.04] to-white/[0.01]"
            style={CARD_SHADOW}
            data-testid="calories-card"
          >
            <img
              src="/calories-flame-bg.webp"
              alt=""
              className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-90"
              loading="lazy"
              decoding="async"
              data-testid="calories-fire-strip"
            />
            <span className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#0B0B0B]/45 via-[#0B0B0B]/25 to-[#0B0B0B]/90" />
            <span className="pointer-events-none absolute inset-x-0 top-1/4 h-1/2" style={{ background: 'radial-gradient(ellipse at center, rgba(8,8,8,0.9) 0%, rgba(8,8,8,0.45) 45%, transparent 75%)' }} />
            <CaloriesCard stats={displayCalorieStats} isDemo={showCalorieDemo} />
          </motion.div>
        </>)}

        {/* ═══ CARDIO — weekly minutes + week streak ═══ */}
        {displayCardioStats && (<>
          <SectionLabel>Cardio</SectionLabel>
          <motion.div
            initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false, amount: 0.2 }} transition={{ delay: 0.2 }}
            className="relative rounded-3xl overflow-hidden border border-white/[0.08] bg-gradient-to-b from-white/[0.04] to-white/[0.01]"
            style={CARD_SHADOW}
            data-testid="cardio-card"
          >
            <img
              src="/cardio-runner-bg.webp"
              alt=""
              className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.72]"
              loading="lazy"
              decoding="async"
              data-testid="cardio-runner-bg"
            />
            <span className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#07090B]/55 via-[#07090B]/40 to-[#07090B]/95" />
            <span className="pointer-events-none absolute inset-x-0 top-1/4 h-1/2" style={{ background: 'radial-gradient(ellipse at center, rgba(7,9,11,0.88) 0%, rgba(7,9,11,0.4) 45%, transparent 75%)' }} />
            <CardioCard stats={displayCardioStats} isDemo={showCardioDemo} />
          </motion.div>
        </>)}

        {/* ═══ RECOVERY THIS WEEK ═══ */}
        <SectionLabel>Recovery</SectionLabel>
        <RecoveryWeekCard />

        {/* ═══ CALIBRATION / ADAPTIVE STATUS CARD ═══ */}
        {/* ═══ SNAPSHOT CAROUSEL (Adaptive · Program) — collapsed until opened ═══ */}
        <button
          onClick={() => setSnapshotOpen((o) => !o)}
          className="w-full flex items-center justify-between gap-3 rounded-2xl border border-white/[0.08] bg-white/[0.02] px-4 py-3.5 active:scale-[0.99] transition-transform"
          aria-expanded={snapshotOpen}
          data-testid="snapshot-toggle"
        >
          <span className="text-left">
            <span className="block font-heading text-base font-black uppercase tracking-tight text-white">
              {isCalibrating ? 'Calibration' : 'Your Snapshot'}
            </span>
            <span className="block mt-0.5 text-xs text-white/40">
              {snapshotOpen ? 'Tap to hide' : 'Split, fatigue, recovery and your programme'}
            </span>
          </span>
          <ChevronDown
            size={20}
            className="shrink-0 text-white/50 transition-transform duration-300"
            style={{ transform: snapshotOpen ? 'rotate(180deg)' : 'none' }}
          />
        </button>

        {snapshotOpen && (
        <StatsCarousel
          testId="home-snapshot-carousel"
          slides={[
            brainStatus && { key: 'adaptive', node: (
              <SnapshotShell bg="/snapshot-adaptive-bg.webp" testId={isCalibrating ? 'calibration-card' : 'brain-status-card'}>
                <SnapshotHeader
                  image="/snapshot-icon-adaptive.webp"
                  titleTop={isCalibrating ? 'Cali' : 'Adaptive'}
                  titleBottom={isCalibrating ? 'bration' : 'Status'}
                  subtitle={isCalibrating ? `Session ${sessionsCompleted} of 4 — learning your limits` : 'Tuned to your last sessions'}
                  chip={<SnapshotChip testId="adaptive-status-chip">{isCalibrating ? `${sessionsCompleted}/4` : 'Active'}</SnapshotChip>}
                />

                {isCalibrating ? (
                  <>
                    <div className="mt-4">
                      <CalibrationBadge completed={sessionsCompleted} testId="calibration-badge-home" />
                      <p className="mt-2 text-[13px] font-medium text-white/50">Complete 4 sessions to unlock your personalised program</p>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-2.5">
                      <SnapshotStat
                        icon={Activity}
                        value={`${calibrationMetrics.completion_rates?.length > 0 ? Math.round(calibrationMetrics.completion_rates.reduce((a, b) => a + b, 0) / calibrationMetrics.completion_rates.length) : 0}%`}
                        label="Completion" sub="Rate" tint chevron={false} testId="stat-card-completion"
                      />
                      <SnapshotStat
                        icon={Zap}
                        value={calibrationMetrics.rpe_history?.length > 0 ? (calibrationMetrics.rpe_history.reduce((a, b) => a + b, 0) / calibrationMetrics.rpe_history.length).toFixed(1) : '-'}
                        label="Average" sub="RPE" chevron={false} testId="stat-card-rpe"
                      />
                      <SnapshotStat icon={Battery} value={`${recoveryScore}%`} label="Recovery" chevron={false} testId="stat-card-recovery" />
                      <SnapshotStat icon={AlertCircle} value={`${fatigueScore}%`} label="Fatigue" tint={fatigueScore > 60} chevron={false} testId="stat-card-fatigue" />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mt-4 grid grid-cols-2 gap-2.5">
                      <SnapshotStat icon={Dumbbell} value={splitType?.replace(/_/g, ' ') || '-'} label="Current" sub="Split" chevron={false} testId="stat-card-split" />
                      <SnapshotStat
                        icon={TrendingUp}
                        value={brainStatus?.volume_targets?.sets_per_muscle_per_week?.optimal || brainStatus?.volume_targets?.optimal_sets || '-'}
                        label="Sets per muscle" sub="Per Week" chevron={false} testId="stat-card-volume"
                      />
                      <SnapshotStat icon={AlertCircle} value={`${fatigueScore}%`} label="Fatigue" sub="Load" tint={fatigueScore > 60} chevron={false} testId="stat-card-fatigue" />
                      <SnapshotStat icon={BatteryCharging} value={`${recoveryScore}%`} label="Recovery" sub="Score" tint chevron={false} testId="stat-card-recovery" />
                    </div>
                    <p className="mt-3.5 flex items-center justify-center gap-1.5 text-center text-[11px] text-white/35" data-testid="fatigue-trend-note">
                      <Activity size={12} className="shrink-0" />
                      Fatigue trend: <span className="font-heading font-black uppercase tracking-wider text-accent">{fatigueTrend}</span>
                    </p>
                  </>
                )}

                {hasFocusAreas && (
                  <div className="mt-3.5 border-t border-white/[0.07] pt-3.5" data-testid="focus-areas-section">
                    <span className="mb-2.5 block font-heading text-[11px] font-black uppercase tracking-[0.16em] text-accent">Muscle Focus Areas</span>
                    <div className="flex flex-wrap gap-2">
                      {focusLabels.map((label) => (
                        <span key={label} className="flex items-center gap-1.5 rounded-lg border border-accent/30 bg-black/20 px-3 py-1.5 backdrop-blur-sm">
                          <Crosshair size={12} className="text-accent" />
                          <span className="font-heading text-[12px] font-bold uppercase tracking-wider text-accent">{label}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </SnapshotShell>
            ) },
            workout && { key: 'program', node: (
              <SnapshotShell bg="/snapshot-program-bg.webp" testId="program-snapshot-card">
                <SnapshotHeader
                  image="/snapshot-icon-program.webp"
                  titleTop="Your"
                  titleBottom="Program"
                  subtitle={`${profile?.training_days || '-'} sessions a week, built for you`}
                  action={<RegenerateButton onClick={generateWorkout} busy={generating} />}
                />
                <div className="mt-4 grid grid-cols-2 gap-2.5">
                  <SnapshotStat icon={Sparkles} value={workout.program_name || 'Custom'} label="Active" sub="Program" tint chevron={false} testId="plan-name" />
                  <SnapshotStat icon={Clock} value={`${profile?.session_length || '-'} Min`} label="Session" sub="Length" chevron={false} testId="session-stat" />
                  <SnapshotStat icon={Activity} value={`${profile?.training_days || '-'} Days`} label="Schedule" sub="Per Week" chevron={false} testId="training-days-stat" />
                  <SnapshotStat icon={Flame} value={formatGoal(profile?.primary_goal) || '-'} label="Primary" sub="Goal" tint chevron={false} testId="goal-stat" />
                </div>
              </SnapshotShell>
            ) },
          ]}
        />
        )}

        {/* ═══ NOTIFICATION PROMPT BANNER ═══ */}
        {showNotifBanner && (
          <div

            className="rounded-2xl border border-white/[0.1] p-4 flex items-center gap-3"
            style={{ background: 'rgba(var(--accent-rgb),0.04)' }}
            data-testid="notif-banner"
          >
            <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
              <Bell size={20} className="text-accent" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-heading font-bold text-white">Get workout reminders</p>
              <p className="text-xs text-white/40">Stay on track with training notifications from your Coach.</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={async () => {
                  try {
                    const { initOneSignal, oneSignalLogin, setOneSignalEmail, requestPushPermission, setOneSignalNotificationMode } = await import('@/utils/oneSignalManager');
                    await initOneSignal();
                    if (user?.id) await oneSignalLogin(user.id);
                    if (user?.email) await setOneSignalEmail(user.email);
                    await requestPushPermission();
                    if (Notification.permission === 'granted') {
                      await setOneSignalNotificationMode('subtle');
                      await api.post('/notifications/settings', { mode: 'subtle', push_enabled: true, preferred_training_times: [] }).catch(() => {});
                    }
                  } catch {}
                  setShowNotifBanner(false);
                  try { localStorage.setItem('notif_banner_dismissed', '1'); } catch {}
                }}
                className="px-4 py-2 rounded-lg bg-accent text-accent-foreground text-xs font-heading font-bold uppercase tracking-wider active:scale-95 transition-transform"
                data-testid="notif-banner-enable"
              >
                Enable
              </button>
              <button
                onClick={() => { setShowNotifBanner(false); try { localStorage.setItem('notif_banner_dismissed', '1'); } catch {} }}
                className="px-3 py-2 rounded-lg bg-white/5 text-white/40 text-xs font-heading font-bold uppercase tracking-wider active:scale-95 transition-transform"
              >
                Later
              </button>
            </div>
          </div>
        )}



        {/* ═══ PROGRAMS CAROUSEL (Booty Max → Build Your Own) ═══ */}
        <SectionLabel>Programs</SectionLabel>
        <ProgramCarousel api={api} />

        {/* ═══ EXPLORE CAROUSEL (what's new in BodyMax) ═══ */}
        <SectionLabel>Explore</SectionLabel>
        <ExploreCarousel />
        {recoveryLog.sheet}


        {/* ═══ Install App Prompt (bottom) ═══ */}
        <DashboardInstallCard
          hasCompletedOnboarding={!!user?.onboarding_complete}
          hasActivePlan={!!workout}
        />

      </main>
    </div>
  );
}
